<template>
  <div class="p-4">
    <div
      class="btn btn-outline-primary mb-4"
      @click="redirectToSupportTopicPicker"
    >
      <i class="fas fa-arrow-left-long bi" />
      {{ $t('back') }}
    </div>

    <h4 class="text-center">
      {{ $t(supportStore.selectedTopic) }}
    </h4>

    <div class="my-3">
      <!-- Optional contact address -->
      <div v-if="contactRequired">
        <label for="email" class="form-label">
          {{ $t('contact_label') }}
        </label>
        <input
          class="form-control mb-3"
          type="email"
          name="email"
          v-model="contactEmail"
        >
      </div>

      <!-- Issue select -->
      <label v-html="$t('issue_label')" for="issue" class="form-label" />
      <select name="issue" v-model="issueName" class="form-select mb-3">
        <option
          v-for="issue in this.supportStore.currentTopicIssues"
          :value="issue.name"
          :key="issue.name"
        >
          {{ issue.description }}
        </option>
      </select>

      <template v-if="issueName">
        <p v-if="issueAnswer" v-html="issueAnswer" class="alert alert-primary" />

        <template v-else>
          <div
            v-for="field in issueFields"
            :key="field.name"
            class="mb-3"
          >
            <label
              v-html="field.description"
              :for="field.name"
              class="form-label"
              v-if="field.type !== 'checkbox'"
            />
            <input
              type="text"
              class="form-control"
              :name="field.name"
              v-model="fieldValues[field.name]"
              v-if="field.type === 'input' ||
                (field.type === 'order' && userOrders.length === 0) ||
                (field.type === 'project' && contactRequired) ||
                (field.type === 'organization' && userOrganizations.length === 0)"
            >
            <div
              v-if="field.type === 'checkbox'"
              class="form-check"
            >
              <input
                type="checkbox"
                class="form-check-input"
                :name="field.name"
                :id="field.name"
                v-model="fieldValues[field.name]"
              >
              <label class="form-check-label" :for="field.name" v-html="field.description" />
            </div>

            <input
              type="tel"
              class="form-control"
              :name="field.name"
              v-model="fieldValues[field.name]"
              v-if="field.type === 'phone'"
            >
            <textarea
              :name="field.name"
              class="form-control"
              rows="5"
              v-model="fieldValues[field.name]"
              v-if="field.type === 'multiline'"
            />
            <select
              :name="field.name"
              class="form-control"
              v-if="field.type === 'order' && userOrders.length > 0"
              v-model="fieldValues[field.name]"
            >
              <option
                v-for="order in userOrders"
                :value="order.token"
                :key=" order.token"
              >
                {{ order.name }}
              </option>
            </select>
            <select
              class="form-select"
              :name="field.name"
              v-if="field.type === 'project' && !contactRequired"
              v-model="fieldValues[field.name]"
            >
              <option value="new_project">
                {{ $t('newProject') }}
              </option>
              <option
                v-for="project in userProjects"
                :value="project.token"
                :key="project.token"
              >
                {{ project.name }}
              </option>
            </select>
            <select
              class="form-select"
              :name="field.name"
              v-if="field.type === 'select'"
              v-model="fieldValues[field.name]"
            >
              <option
                v-for="option in field.options"
                :value="option.value"
                :key=" option.value"
              >
                {{ option.human }}
              </option>
            </select>
            <div
              class="form-text"
              v-if="field.type === 'project' &&
                !contactRequired && fieldValues.project === 'new_project'"
            >
              {{ $t('newProjectHint') }}
            </div>

            <select
              class="form-select"
              v-if="field.type === 'organization' && userOrganizations.length > 0"
              v-model="fieldValues[field.name]"
            >
              <option
                v-for="organization in userOrganizations"
                :value="organization.name"
                :key="organization.slug"
              >
                {{ organization.name }}
              </option>
            </select>

            <div class="row g-3" v-if="field.type === 'attachment'">
              <div class="col col-sm-12 col-lg-6">
                <input
                  :name="field.name"
                  type="file"
                  @change="onFileUpload"
                  multiple
                  class="form-control"
                >
              </div>
              <div class="col col-sm-12 col-lg-6">
                <ul class="list-group">
                  <li
                    class="list-group-item"
                    v-for="(file, index) in fieldValues.attachment"
                    :key="file.name"
                  >
                    <div class="row">
                      <span class="text-truncate col">
                        {{ file.name }}
                      </span>

                      <span class="col-auto text-end">
                        <span class="badge text-bg-secondary me-3">{{ getSize(file.size) }}</span>
                        <button
                          class="btn btn-outline-danger btn-sm"
                          type="button"
                          @click="removeFile(index)"
                        >
                          <span aria-hidden="true">&times;</span>
                        </button>
                      </span>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div class="text-end">
            <button
              v-if="!issueAnswer"
              class="btn btn-aisler-gradient btn-lg"
              @click="submit"
              :disabled="!isSubmittable"
            >
              {{ $t('submit') }}
            </button>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import { components } from 'locales';
import { postRequest } from 'helper/request';
import { useSupportStore } from 'store/support';
import { mapState } from 'pinia';

export default {
  name: 'SupportForm',
  props: {
    createSupportPath: String,
  },
  data() {
    return {
      availableIssues: [],
      contactEmail: '',
      issueName: null,
      fieldValues: {
        project: 'new_project',
        phone_number: '',
      },
      processing: false,
    };
  },
  setup() {
    const supportStore = useSupportStore();
    return { supportStore };
  },
  watch: {
    issueName(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.supportStore.selectIssue(newValue);
      }
    },
  },
  computed: {
    ...mapState(useSupportStore, [
      'selectedIssue', 'isContactMissing', 'userOrders', 'userOrganizations', 'userProjects', 'userPhone',
    ]),
    issueFields() {
      if (!this.selectedIssue) return [];

      return this.selectedIssue.fields;
    },
    issueAnswer() {
      return this.selectedIssue?.answer;
    },
    contactRequired() {
      return this.isContactMissing && !this.issueAnswer;
    },
    isSubmittable() {
      return !this.processing && this.valid;
    },
    valid() {
      if (this.issueName === null) {
        return false;
      }

      return this.issueFields.every((field) => {
        const value = this.fieldValues[field.name];
        if (field.name === 'attachment') return true;
        if (this.issueName === 'assembly_quoting' && field.name === 'project') return true;
        return value !== null && value !== undefined;
      });
    },
  },
  methods: {
    payload() {
      const fields = {};

      if (this.isContactMissing) {
        fields.contact_email = this.contactEmail;
      }

      // Collect actual relevant fields for issue
      this.issueFields.forEach((field) => {
        fields[field.name] = this.fieldValues[field.name];
      });

      return { issue: { name: this.issueName, fields } };
    },
    resetForm() {
      this.issueName = null;
      this.contactEmail = '';
      this.fieldValues = {};
    },
    async onFileUpload(event) {
      const newUploadedFiles = event.target.files;
      const oldAttachments = this.fieldValues.attachment;
      let filesToBeAdded = [];
      let contentPromises = [];

      for (const file of newUploadedFiles) {
        const {
          name,
          size,
          type,
        } = file;
        contentPromises = [...contentPromises, this.readFileContent(file)];
        filesToBeAdded = [...filesToBeAdded,
          {
            name,
            size,
            type,
          },
        ];
      }

      // based on https://eslint.org/docs/rules/no-await-in-loop
      const contents = await Promise.all(contentPromises);
      for (let i = 0; i < contents.length; i += 1) {
        filesToBeAdded[i] = { ...filesToBeAdded[i], content: JSON.stringify(contents[i]).split(',')[1] };
      }

      const newAttachments =
        oldAttachments ? [...oldAttachments, ...filesToBeAdded] : filesToBeAdded;
      this.fieldValues = { ...this.fieldValues, attachment: newAttachments };
    },
    readFileContent(file) {
      const reader = new FileReader();
      return new Promise((resolve, reject) => {
        reader.onload = (event) => resolve(event.target.result);
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
      });
    },
    getSize(size) {
      const sizes = [' Bytes', ' KB', ' MB', ' GB',
        ' TB', ' PB', ' EB', ' ZB', ' YB'];

      for (let i = 1; i < sizes.length; i += 1) {
        if (size < (1024 ** i)) {
          return (Math.round(((size / (1024 ** (i - 1))) * 100) / 100) + sizes[i - 1]);
        }
      }
      return size;
    },
    removeFile(index) {
      this.fieldValues.attachment.splice(index, 1);
    },
    async submit() {
      this.processing = true;
      this.supportStore.success = null;

      try {
        await postRequest(this.createSupportPath, { data: this.payload() });

        this.processing = false;
        this.resetForm();
        this.supportStore.success = true;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {
        this.processing = false;
        this.supportStore.success = false;
      }
    },
    redirectToSupportTopicPicker() {
      this.supportStore.resetSelections();
    },
  },
  async mounted() {
    this.issueName = this.supportStore.selectedIssueName;

    if (this.isContactMissing) this.fieldValues.project = '';
    if (this.userPhone) this.fieldValues.phone_number = this.userPhone;
  },
  i18n: components.support.SupportForm,
};

</script>

<style lang="scss" scoped>

</style>
